import axios from "axios";

// Base URL for the logging API (separate project)
const LOGGING_API_BASE_URL = import.meta.env.VITE_LOGGING_API_BASE_URL || "https://apcglogs.erpaga.com/api";

// Create a separate axios instance for logging API
const loggingApiClient = axios.create({
  baseURL: LOGGING_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
});

/**
 * Interface for API Error Log
 */
export interface ApcgApiErrorLog {
  Issue?: string;
  Reason?: string;
  IpAddress?: string;
  Country?: string;
  ApiEndpoint?: string;
  DropdownName?: string;
  CreatedDate?: Date;
  UserAgent?: string;
}

/**
 * Interface for Registration Log
 */
export interface ApcgRegistrationLog {
  Name?: string;
  Email?: string;
  MobileNo?: string;
  Issue?: string;
  IpAddress?: string;
  Country?: string;
  Reason?: string;
  CreatedDate?: Date;
  UserAgent?: string;
}

/**
 * Get user's IP address using a third-party service
 */
export const getUserIPAddress = async (): Promise<string> => {
  try {
    // Try multiple IP detection services
    const ipServices = [
      { url: "https://api.ipify.org?format=json", isJson: true },
      { url: "https://ipapi.co/json/", isJson: true },
      { url: "https://api.ip.sb/ip", isJson: false },
    ];

    for (const service of ipServices) {
      try {
        const response = await fetch(service.url, { 
          method: "GET",
          signal: AbortSignal.timeout(3000) // 3 second timeout
        });
        
        if (service.isJson) {
          const data = await response.json();
          if (data.ip) {
            return data.ip;
          }
        } else {
          const text = await response.text();
          const ip = text.trim();
          if (ip && ip !== "") {
            return ip;
          }
        }
      } catch (err) {
        // Try next service
        continue;
      }
    }
    
    return "Unknown";
  } catch (error) {
    return "Unknown";
  }
};

/**
 * Get user's country from IP using multiple services for better accuracy
 * Returns whatever country name/code the geolocation service provides (no hardcoded mapping)
 */
export const getUserCountry = async (): Promise<string> => {
  try {
    // Try multiple geolocation services for better accuracy
    const geoServices = [
      {
        url: "https://ipapi.co/json/",
        getCountry: (data: any) => {
          // Prefer country_name (full name) over country code
          return data.country_name || data.country || null;
        },
      },
      {
        url: "https://ip-api.com/json/?fields=status,message,country,countryCode",
        getCountry: (data: any) => {
          // ip-api.com returns full country name in 'country' field
          return data.country || data.countryCode || null;
        },
      },
      {
        url: "https://api.ipgeolocation.io/ipgeo?apiKey=free",
        getCountry: (data: any) => {
          return data.country_name || data.country_code || null;
        },
      },
      {
        url: "https://ipinfo.io/json",
        getCountry: (data: any) => {
          // ipinfo.io returns country code, but try country_name first if available
          return data.country_name || data.country || null;
        },
      },
    ];

    for (const service of geoServices) {
      try {
        const response = await fetch(service.url, {
          method: "GET",
          signal: AbortSignal.timeout(3000),
        });
        
        if (!response.ok) continue;
        
        const data = await response.json();
        const country = service.getCountry(data);
        
        if (country && country !== "Unknown" && country.trim() !== "") {
          // Return whatever the service provides (no hardcoded conversion)
          return country;
        }
      } catch (err) {
        // Try next service
        continue;
      }
    }
    
    return "Unknown";
  } catch (error) {
    return "Unknown";
  }
};

/**
 * Get user agent from browser
 */
export const getUserAgent = (): string => {
  return navigator.userAgent || "Unknown";
};

/**
 * Parse User Agent string to extract browser, OS, and device information
 */
export const parseUserAgent = (userAgent: string): {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: string;
  fullInfo: string;
} => {
  const ua = userAgent || "";
  
  let browser = "Unknown";
  let browserVersion = "";
  let os = "Unknown";
  let osVersion = "";
  let device = "Desktop";
  
  // Detect Browser
  if (ua.includes("Chrome") && !ua.includes("Edg") && !ua.includes("OPR")) {
    const chromeMatch = ua.match(/Chrome\/([\d.]+)/);
    browser = "Chrome";
    browserVersion = chromeMatch ? chromeMatch[1] : "";
  } else if (ua.includes("Firefox")) {
    const firefoxMatch = ua.match(/Firefox\/([\d.]+)/);
    browser = "Firefox";
    browserVersion = firefoxMatch ? firefoxMatch[1] : "";
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    const safariMatch = ua.match(/Version\/([\d.]+).*Safari/);
    browser = "Safari";
    browserVersion = safariMatch ? safariMatch[1] : "";
  } else if (ua.includes("Edg")) {
    const edgMatch = ua.match(/Edg\/([\d.]+)/);
    browser = "Edge";
    browserVersion = edgMatch ? edgMatch[1] : "";
  } else if (ua.includes("OPR")) {
    const operaMatch = ua.match(/OPR\/([\d.]+)/);
    browser = "Opera";
    browserVersion = operaMatch ? operaMatch[1] : "";
  } else if (ua.includes("MSIE") || ua.includes("Trident")) {
    browser = "Internet Explorer";
    const ieMatch = ua.match(/(?:MSIE |rv:)([\d.]+)/);
    browserVersion = ieMatch ? ieMatch[1] : "";
  }
  
  // Detect Operating System
  if (ua.includes("Windows NT 10.0")) {
    os = "Windows";
    osVersion = "10";
  } else if (ua.includes("Windows NT 6.3")) {
    os = "Windows";
    osVersion = "8.1";
  } else if (ua.includes("Windows NT 6.2")) {
    os = "Windows";
    osVersion = "8";
  } else if (ua.includes("Windows NT 6.1")) {
    os = "Windows";
    osVersion = "7";
  } else if (ua.includes("Mac OS X")) {
    const macMatch = ua.match(/Mac OS X ([\d_]+)/);
    os = "macOS";
    osVersion = macMatch ? macMatch[1].replace(/_/g, ".") : "";
  } else if (ua.includes("Linux")) {
    os = "Linux";
    osVersion = "";
  } else if (ua.includes("Android")) {
    const androidMatch = ua.match(/Android ([\d.]+)/);
    os = "Android";
    osVersion = androidMatch ? androidMatch[1] : "";
    device = "Mobile";
  } else if (ua.includes("iPhone")) {
    const iosMatch = ua.match(/OS ([\d_]+)/);
    os = "iOS";
    osVersion = iosMatch ? iosMatch[1].replace(/_/g, ".") : "";
    device = "Mobile";
  } else if (ua.includes("iPad")) {
    const iosMatch = ua.match(/OS ([\d_]+)/);
    os = "iOS";
    osVersion = iosMatch ? iosMatch[1].replace(/_/g, ".") : "";
    device = "Tablet";
  }
  
  // Detect Architecture (64-bit or 32-bit)
  let arch = "";
  if (ua.includes("Win64") || ua.includes("x64") || ua.includes("WOW64")) {
    arch = "64-bit";
  } else if (ua.includes("Win32") || ua.includes("x86")) {
    arch = "32-bit";
  }
  
  // Create full readable info
  const browserInfo = browserVersion ? `${browser} ${browserVersion}` : browser;
  const osInfo = osVersion ? `${os} ${osVersion}` : os;
  const fullInfo = `${browserInfo} on ${osInfo}${arch ? ` (${arch})` : ""} - ${device}`;
  
  return {
    browser,
    browserVersion,
    os,
    osVersion,
    device,
    fullInfo
  };
};

/**
 * Extract detailed error information from axios error
 */
export const extractErrorDetails = (error: any): { issue: string; reason: string } => {
  let issue = "Unknown Error";
  let reason = "An unexpected error occurred";

  if (!error) {
    return { issue: "Unknown Error", reason: "No error details available" };
  }

  // Network errors (no internet, CORS, timeout)
  if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
    issue = "Network Connection Failed";
    reason = "Unable to connect to the server. Please check your internet connection and try again.";
  }
  // Timeout errors
  else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
    issue = "Request Timeout";
    reason = "The request took too long to complete. The server may be slow or unavailable. Please try again.";
  }
  // HTTP status code errors
  else if (error.response) {
    const status = error.response.status;
    const statusText = error.response.statusText || "";
    const data = error.response.data;

    switch (status) {
      case 400:
        issue = "Invalid Request Data";
        reason = data?.message || data?.error || `Bad Request: ${statusText}. The data sent to the server was invalid or incomplete.`;
        break;
      case 401:
        issue = "Authentication Failed";
        reason = data?.message || data?.error || `Unauthorized: ${statusText}. Authentication required or invalid credentials.`;
        break;
      case 403:
        issue = "Access Forbidden";
        reason = data?.message || data?.error || `Forbidden: ${statusText}. You don't have permission to access this resource.`;
        break;
      case 404:
        issue = "API Endpoint Not Found";
        reason = data?.message || data?.error || `Not Found: ${statusText}. The requested API endpoint does not exist.`;
        break;
      case 500:
        issue = "Internal Server Error";
        reason = data?.message || data?.error || `Server Error: ${statusText}. The server encountered an error while processing your request.`;
        break;
      case 502:
        issue = "Bad Gateway";
        reason = data?.message || data?.error || `Bad Gateway: ${statusText}. The server received an invalid response from upstream server.`;
        break;
      case 503:
        issue = "Service Unavailable";
        reason = data?.message || data?.error || `Service Unavailable: ${statusText}. The server is temporarily unavailable. Please try again later.`;
        break;
      case 504:
        issue = "Gateway Timeout";
        reason = data?.message || data?.error || `Gateway Timeout: ${statusText}. The server did not receive a timely response from upstream server.`;
        break;
      default:
        issue = `HTTP Error ${status}`;
        reason = data?.message || data?.error || `HTTP ${status}: ${statusText}. An error occurred while processing your request.`;
    }

    // Add additional error details if available
    if (data?.errors && typeof data.errors === "object") {
      const errorMessages = Object.values(data.errors).flat().join("; ");
      if (errorMessages) {
        reason += ` Details: ${errorMessages}`;
      }
    }
  }
  // Request configuration errors
  else if (error.request) {
    issue = "Request Failed";
    reason = "The request was made but no response was received. The server may be down or unreachable.";
  }
  // Generic error messages
  else if (error.message) {
    issue = "Request Error";
    reason = error.message;
  }

  return { issue, reason };
};

/**
 * Save API Error Log (for dropdown errors)
 */
export const saveApiErrorLog = async (errorLog: ApcgApiErrorLog): Promise<void> => {
  try {
    // Get user IP and country if not provided
    if (!errorLog.IpAddress || errorLog.IpAddress === "Unknown") {
      errorLog.IpAddress = await getUserIPAddress();
    }
    
    if (!errorLog.Country || errorLog.Country === "Unknown") {
      errorLog.Country = await getUserCountry();
    }
    
    // Get and parse User Agent for readable format
    const userAgentString = getUserAgent();
    if (!errorLog.UserAgent) {
      const parsedUA = parseUserAgent(userAgentString);
      // Save both full User Agent and parsed readable info
      errorLog.UserAgent = `${parsedUA.fullInfo} | Full UA: ${userAgentString}`;
    }
    
    if (!errorLog.CreatedDate) {
      errorLog.CreatedDate = new Date();
    }

    await loggingApiClient.post("/ApcgRegistrationLog/save-api-error-log", errorLog);
  } catch (error) {
    // Silently fail - don't break the main flow if logging fails
    console.error("Failed to save API error log:", error);
  }
};

/**
 * Save Registration Log (for registration errors)
 */
export const saveRegistrationLog = async (registrationLog: ApcgRegistrationLog): Promise<void> => {
  try {
    // Get user IP and country if not provided
    if (!registrationLog.IpAddress || registrationLog.IpAddress === "Unknown") {
      registrationLog.IpAddress = await getUserIPAddress();
    }
    
    if (!registrationLog.Country || registrationLog.Country === "Unknown") {
      registrationLog.Country = await getUserCountry();
    }
    
    // Get and parse User Agent for readable format
    const userAgentString = getUserAgent();
    if (!registrationLog.UserAgent) {
      const parsedUA = parseUserAgent(userAgentString);
      // Save both full User Agent and parsed readable info
      registrationLog.UserAgent = `${parsedUA.fullInfo} | Full UA: ${userAgentString}`;
    }
    
    if (!registrationLog.CreatedDate) {
      registrationLog.CreatedDate = new Date();
    }

    await loggingApiClient.post("/ApcgRegistrationLog/save-registration-log", registrationLog);
  } catch (error) {
    // Silently fail - don't break the main flow if logging fails
    console.error("Failed to save registration log:", error);
  }
};

